import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  ClipboardCheck,
  FileText,
  Calendar,
  User,
  Info,
  MapPin,
  Download,
  FileSearch,
  Play,
  Pause,
  CheckCircleIcon,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { useToast } from "@shared/hooks/use-toast";
import ApplicationHeader from "./ApplicationHeader";
import MainLayout from "./MainLayout";
import { useWorkflow } from "./WorkflowContext";
import WorkflowProgress from "./WorkflowProgress";
import {
  useServiceOrderByConsumer,
  useUpdateConsumerApprovalStatus,
  useDownloadReport,
  useUpdateStatus,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { Badge } from "@shared/ui/badge";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";

const ApprovalPage = () => {
  const { consumerId: consumerId } = useParams<{ consumerId: string }>();
  const remoteUtilityId = getRemoteUtilityId();
  const {
    application,
    workflowSteps,
    moveToNextStep,
    updateApplicationStatus,
    toggleInspectionRequirement,
  } = useWorkflow();
  const [approvalDecision, setApprovalDecision] = useState<
    "approve" | "reject" | null
  >(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [requirements, setRequirements] = useState({
    inspectionPassed: application?.additionalData?.isSiteInspection || false,
    paymentReceived: application?.additionalData?.kyc || false,
    technicalRequirements:
      application?.additionalData?.isPaymentApprove || false,
    legalRequirements: application?.additionalData?.isLegalReqMet || false,
  });
  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Service Connection Workflow");
  const navigate = useNavigate();
  const { toast } = useToast();

  // Initialize the hooks
  const updateConsumerApprovalStatus = useUpdateConsumerApprovalStatus();
  const updateStatus = useUpdateStatus(Number(consumerId));
  const downloadReport = useDownloadReport();
  
  console.log('🔍 ApprovalPage - Hook parameters:', {
    remoteUtilityId,
    consumerId,
    tab_name: "Survey_Inprogress",
    application: !!application
  });
  
  const {
    data: serviceOrderData,
    isLoading: isLoadingServiceOrder,
    error: serviceOrderError,
  } = useServiceOrderByConsumer({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerId,
    tab_name: "Survey_Inprogress",
  });
  
  console.log('🔍 ApprovalPage - Query result:', {
    serviceOrderData,
    isLoadingServiceOrder,
    serviceOrderError
  });

  const reportId =
    serviceOrderData?.result?.id || serviceOrderData?.result?.jobCardNo;

  const viewReport = useDownloadReport();

  useEffect(() => {
    if (application) {
      startTracking();
      setRequirements((prev) => ({
        ...prev,
        inspectionPassed: application.requiresInspection
          ? prev.inspectionPassed
          : true,
      }));
    }
  }, [application?.requiresInspection]);

  console.log('🔍 ApprovalPage - Component render check:', {
    hasApplication: !!application,
    applicationData: application
  });

  if (!application) {
    console.log('⚠️ ApprovalPage - Early return due to missing application');
    return <div>Loading...</div>;
  }

  const allRequirementsMet = Object.values(requirements).every(Boolean);

  const handleViewReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      const blob = await viewReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast({
        title: "Error viewing report",
        description: "Failed to open the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      await downloadReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      toast({
        title: "Download started",
        description: "The inspection report is being downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleApprove = async () => {
    if (!allRequirementsMet) {
      toast({
        title: "Cannot approve",
        description: "All requirements must be met before approval.",
        variant: "destructive",
      });
      return;
    }

    if (approvalDecision !== "approve") {
      toast({
        title: "Approval decision required",
        description: "Please select 'Approve' to proceed with approval.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload: any = {
        additional_data: {
          is_site_inspection: requirements.inspectionPassed,
          kyc: requirements.paymentReceived,
          is_payment_approve: requirements.technicalRequirements,
          is_legal_req_met: requirements.legalRequirements,
        },
      };

      await updateConsumerApprovalStatus.mutateAsync(
        {
          consumerId: consumerId,
          payload: payload,
        },
        {
          onSuccess: () => {
            logTimeUntilStep("Approval");
          },
        }
      );

      updateApplicationStatus("installation");
      moveToNextStep();

      toast({
        title: "Application approved",
        description:
          "The application has been approved and is ready for meter activation.",
      });

      navigate(`/cx/consumer-onboarding/workflow/installation/${consumerId}/INSTALLATION INPROGRESS`);
    } catch (error) {
      console.error("Failed to approve application:", error);
      toast({
        title: "Approval failed",
        description: "Failed to approve the application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReject = async () => {
    if (approvalDecision !== "reject") {
      toast({
        title: "Rejection decision required",
        description: "Please select 'Reject' to proceed with rejection.",
        variant: "destructive",
      });
      return;
    }

    if (!approvalNotes.trim()) {
      toast({
        title: "Rejection reason required",
        description: "Please provide a reason for rejection in the notes.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload: any = {
        is_application_reject: 1,
        remark: approvalNotes.trim(),
      };

      await updateConsumerApprovalStatus.mutateAsync({
        consumerId: consumerId,
        payload: payload,
      });

      updateApplicationStatus("rejected");

      toast({
        title: "Application rejected",
        description: "The application has been rejected.",
        variant: "destructive",
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to reject application:", error);
      toast({
        title: "Rejection failed",
        description: "Failed to reject the application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    navigate(`/cx/consumer-onboarding/workflow/inspection/${consumerId}`);
  };

  const toggleInspection = (required: boolean) => {
    toggleInspectionRequirement(required);
    if (!required) {
      setRequirements((prev) => ({
        ...prev,
        inspectionPassed: true,
      }));
    }
  };

  // Map API response to inspection data format
  const inspectionData = serviceOrderData?.result
    ? {
        serviceOrderName: serviceOrderData.result.sop?.name || "N/A",
        description: `${serviceOrderData.result.sop?.sopType || ""} - ${
          serviceOrderData.result.sop?.sop_subtype || "N/A"
        }`,
        type: serviceOrderData.result?.sopTypeDisplay || "N/A",
        subType: serviceOrderData.result?.sopSubtypeDisplay || "N/A",
        createdOn: serviceOrderData.result.createdDate || "N/A",
        scheduledOn: serviceOrderData.result.expectedEndDate || "N/A",
        assignedOn: serviceOrderData.result.assignDate || "N/A",
        assignedTo: serviceOrderData?.result?.fieldForce?.fieldForceNo || "N/A",
        status: serviceOrderData.result.statusDisplay || "N/A",
        serviceAddress: serviceOrderData.result.consumerAddress || "N/A",
        jobCardNo: serviceOrderData.result.jobCardNo || "N/A",
        requestAccountNo: serviceOrderData.result.requestAccountNo || "N/A",
        mobileNumber: serviceOrderData.result.mobileNumber || "N/A",
        requestUsername: serviceOrderData.result.requestUsername || "N/A",
      }
    : null;


  // Determine if inspection is complete/passed based on status
  const isInspectionPassed =
    serviceOrderData?.result?.consumerStatus === 1 &&
    serviceOrderData?.result?.consumerStatusDisplay === "APPROVED";

  const handleAcknowledge = () => {
    // update the status in the database
    const acknowledge_date = new Date().toISOString();
    updateStatus.mutate({
      remote_utility_id: remoteUtilityId,
      progress_status: "IN_PROGRESS",
      acknowledge_date,
    });
    toast({
      title: "Application acknowledged",
      description:
        "The application has been acknowledged and is now in progress.",
    });
  };

  const handlePutOnHold = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "HOLD",
      });

      toast({
        title: "Application on hold",
        description: "The application has been put on hold.",
      });
    } catch (error) {
      console.error("Error putting application on hold:", error);
      toast({
        title: "Error",
        description: "Failed to put application on hold. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResume = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "IN_PROGRESS",
      });

      toast({
        title: "Application resumed",
        description: "The application has been resumed and is now in progress.",
      });
    } catch (error) {
      console.error("Error resuming application:", error);
      toast({
        title: "Error",
        description: "Failed to resume application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        application.progressStatusDisplay === "Acknowledge Pending",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        application.progressStatusDisplay === "In Progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => application.progressStatusDisplay === "Hold",
    },
  ];

  const relevantActions = actionButtons.filter(
    (action) =>
      !action.condition || action.condition(application.progressStatusDisplay)
  );

  function getStatusClasses(status) {
    switch (status) {
      case "Acknowledge Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "In Progress":
        return "bg-green-100 text-green-800 border-green-300";
      case "Hold":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <p className="page-title">
          Service Connection Workflow{" "}
          <Badge
            className={getStatusClasses(application.progressStatusDisplay)}
          >
            {application.progressStatusDisplay}
          </Badge>
        </p>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <WorkflowProgress
        steps={workflowSteps}
        currentStepId={application.currentStep}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
          <div className="lg:col-span-2">
            {/* Only show inspection results if inspection was required AND performed AND data is available */}
            {application.requiresInspection &&
            Object.keys(inspectionData || {}).length > 0 &&
              application.apiStatus.toLowerCase() !== "approval inprogress" && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardCheck className="h-5 w-5 text-workflow-blue" />
                      Inspection Results
                    </CardTitle>
                    <CardDescription>
                      Review inspection results and make a final approval
                      decision
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingServiceOrder ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-sm text-gray-500">
                          Loading inspection data...
                        </div>
                      </div>
                    ) : serviceOrderError ? (
                      <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                        <div className="flex items-start">
                          <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
                          <div>
                            <h3 className="font-medium">
                              Error Loading Inspection Data
                            </h3>
                            <p className="text-sm text-gray-600">
                              Unable to load inspection results. Please try
                              refreshing the page.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          className={`border rounded-md p-4 mb-4 ${
                            isInspectionPassed
                              ? "bg-green-50 border-green-200"
                              : "bg-yellow-50 border-yellow-200"
                          }`}
                        >
                          <div className="flex items-start">
                            {isInspectionPassed ? (
                              <CheckCircle className="h-5 w-5 text-workflow-green mt-0.5 mr-2" />
                            ) : (
                              <Info className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                            )}
                            <div>
                              <h3 className="font-medium">
                                {isInspectionPassed
                                  ? "Inspection Completed Successfully"
                                  : `Inspection Status: ${inspectionData.status}`}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {isInspectionPassed
                                  ? "Site inspection confirmed the location meets all technical requirements."
                                  : "Please review the current inspection status and details below."}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <Table className="text-sm">
                              <TableHeader>
                                <TableRow>
                                  <TableHead
                                    colSpan={2}
                                    className="text-sm font-medium"
                                  >
                                    Service Details
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Service Order
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.serviceOrderName}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    SO Id
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.jobCardNo}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Type
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.type}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Sub Type
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.subType}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>

                            <Table className="text-sm">
                              <TableHeader>
                                <TableRow>
                                  <TableHead
                                    colSpan={2}
                                    className="text-sm font-medium"
                                  >
                                    Schedule Information
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Created On
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.createdOn}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Scheduled On
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.scheduledOn}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Assigned On
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.assignedOn ||
                                      "Not assigned"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Assigned To
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {inspectionData.assignedTo}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell className="font-medium py-2">
                                    Status
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-xs ${
                                        isInspectionPassed
                                          ? "bg-green-100 text-green-800"
                                          : "bg-yellow-100 text-yellow-800"
                                      }`}
                                    >
                                      {inspectionData.status}
                                    </span>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>

                          {/* Report Action Buttons */}
                          <div className="flex gap-2 pt-4">
                            <Button
                              variant="outline"
                              onClick={handleViewReport}
                              disabled={!reportId || viewReport.isPending}
                              className="flex items-center gap-2"
                            >
                              <FileSearch className="h-4 w-4" />
                              {viewReport.isPending
                                ? "Loading..."
                                : "View Report"}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleDownloadReport}
                              disabled={!reportId || downloadReport.isPending}
                              className="flex items-center gap-2"
                            >
                              <Download className="h-4 w-4" />
                              {downloadReport.isPending
                                ? "Downloading..."
                                : "Download Report"}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

            {/* Show message when inspection was skipped */}
            {application.requiresInspection && !inspectionData && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5 text-blue-600" />
                    Inspection Skipped
                  </CardTitle>
                  <CardDescription>
                    Site inspection was skipped for this application
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                      <div>
                        <h3 className="font-medium text-blue-800">
                          Inspection Not Performed
                        </h3>
                        <p className="text-sm text-blue-700 mt-1">
                          The site inspection step was skipped for this
                          application. You can proceed with the approval
                          decision based on the available documentation and
                          application details.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="mt-6">
              <Card>
                <CardHeader>
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="h-5 w-5 " />
                      <CardTitle>Approval Decision</CardTitle>
                    </div>
                    <CardDescription className="ml-7">
                      Make a final decision to approve or reject this
                      application
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-6 mt-4">
                    <RadioGroup
                      value={approvalDecision || ""}
                      onValueChange={(value) =>
                        setApprovalDecision(value as "approve" | "reject")
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="approve" id="approve" />
                        <Label htmlFor="approve" className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-workflow-green mr-2" />
                          Approve Application
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 mt-2">
                        <RadioGroupItem value="reject" id="reject" />
                        <Label htmlFor="reject" className="flex items-center">
                          <XCircle className="h-4 w-4 text-workflow-red mr-2" />
                          Reject Application
                        </Label>
                      </div>
                    </RadioGroup>

                    <div>
                      <h3 className="text-sm font-medium mb-2">
                        Approval Notes
                      </h3>
                      <Textarea
                        placeholder="Add notes regarding your decision..."
                        value={approvalNotes}
                        onChange={(e) => setApprovalNotes(e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-0 justify-between mt-3">
                  <Button variant="outline" onClick={handleBack}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="border-workflow-red text-workflow-red hover:bg-red-50"
                      onClick={handleReject}
                      disabled={
                        updateConsumerApprovalStatus.isPending ||
                        !["survey inprogress", "survey completed"].includes(
                          application?.apiStatus?.toLowerCase() || ""
                        ) ||
                        application.progressStatusDisplay === "Hold"
                      }
                    >
                      {updateConsumerApprovalStatus.isPending &&
                      approvalDecision === "reject"
                        ? "Rejecting..."
                        : "Reject Application"}
                    </Button>
                    <Button
                      onClick={handleApprove}
                      disabled={
                        updateConsumerApprovalStatus.isPending ||
                        ![
                          "survey inprogress",
                          "survey completed",
                          "approval inprogress",
                        ].includes(
                          application?.apiStatus?.toLowerCase() || ""
                        ) ||
                        application.progressStatusDisplay === "Hold"
                      }
                    >
                      {updateConsumerApprovalStatus.isPending &&
                      approvalDecision === "approve"
                        ? "Approving..."
                        : "Approve Application"}
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Approval Requirements</CardTitle>
              <CardDescription className="">
                All requirements must be met for approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 ">
                {application.requiresInspection && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="inspectionPassed"
                      checked={
                        inspectionData ? requirements.inspectionPassed : true
                      }
                      onCheckedChange={(checked) =>
                        setRequirements((prev) => ({
                          ...prev,
                          inspectionPassed: checked === true,
                        }))
                      }
                      disabled={!inspectionData}
                    />
                    <div className="grid">
                      <Label
                        htmlFor="inspectionPassed"
                        className="text-sm font-medium"
                      >
                        {inspectionData
                          ? "Site inspection passed"
                          : "Site inspection (skipped)"}
                      </Label>
                      <p className="text-xs text-gray-500">
                        {inspectionData
                          ? "Verify that all inspection points have been completed successfully"
                          : "Inspection was skipped for this application"}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="paymentReceived"
                    checked={requirements.paymentReceived}
                    onCheckedChange={(checked) =>
                      setRequirements((prev) => ({
                        ...prev,
                        paymentReceived: checked === true,
                      }))
                    }
                  />
                  <div className="grid">
                    <Label
                      htmlFor="paymentReceived"
                      className="text-sm font-medium"
                    >
                      Payment received
                    </Label>
                    <p className="text-xs text-gray-500">
                      Confirm that all applicable fees have been paid in full
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="technicalRequirements"
                    checked={requirements.technicalRequirements}
                    onCheckedChange={(checked) =>
                      setRequirements((prev) => ({
                        ...prev,
                        technicalRequirements: checked === true,
                      }))
                    }
                  />
                  <div className="grid">
                    <Label
                      htmlFor="technicalRequirements"
                      className="text-sm font-medium"
                    >
                      Technical requirements met
                    </Label>
                    <p className="text-xs text-gray-500">
                      Verify that the installation meets all technical
                      specifications
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="legalRequirements"
                    checked={requirements.legalRequirements}
                    onCheckedChange={(checked) =>
                      setRequirements((prev) => ({
                        ...prev,
                        legalRequirements: checked === true,
                      }))
                    }
                  />
                  <div className="grid">
                    <Label
                      htmlFor="legalRequirements"
                      className="text-sm font-medium"
                    >
                      Legal requirements met
                    </Label>
                    <p className="text-xs text-gray-500">
                      Confirm compliance with all applicable regulations and
                      permits
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ApprovalPage;
